// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportStatus, TySupportTicket } from "@/types";
import { formatDateTime, readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import SupportStepper from "@/UI/components/SupportStepper";
import TextInput from "@/UI/inputs/TextInput";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
export type TySupportTicketDetails = {
    ticket: TySupportTicket;
    /** The list behind this panel reloads on it — the status is written the moment it is confirmed. */
    onStatus: (status: TySupportStatus) => void;
    onDone: () => void;
    /** Goes to the person's own page. Absent when the account behind the ticket is gone. */
    onClient?: () => void;
};

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * One ticket, whole, and the one place it is dealt with: who asked, what they asked, where it has got
 * to, and the box the answer is written in.
 *
 * **One panel rather than two.** An admin reads a ticket in order to answer it, so splitting the
 * reading from the writing made them open the same ticket twice — and the answer is written while the
 * question is still on the screen, which is the whole reason it is under it here.
 *
 * The subject and the message are shown, never edited. They are the company's own words, and a vendor
 * who can rewrite a complaint before answering it has no record of what was complained about. The
 * status is the exception to "one save": it is written the moment its confirmation is accepted,
 * because it is also moved from the tile and the two must mean the same thing.
 */
export default function SupportTicketPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { ticket, onStatus, onDone, onClient } = (details ?? {}) as TySupportTicketDetails;

    /** The list reloads behind this panel, but the ticket it was opened with does not. */
    const [status, setStatus] = useState<TySupportStatus>(ticket.status);
    const [reply, setReply] = useState(ticket.reply ?? "");

    const save = useFetch(`/support/${ticket._id}`, { method: "PATCH" }, false);

    useEffect(() => {
        if (!save.succeededAt) return;
        onDone?.();
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    const company = ticket.organization ? readLangValue(ticket.organization.name, lang) || ticket.organization.slug : t.support.unknownCompany;
    const who = ticket.client?.name ?? t.support.unknownPerson;
    const phone = ticket.client?.phone ?? ticket.meta?.clientPhone ?? null;

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.support.detailsTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {/* Who asked, as their own card — and the way to their page, where a password is
                    issued and the rest of their tickets are. Dead rather than absent when the
                    account is gone, so the panel does not change shape between two tickets. */}
                <button
                    type="button"
                    className="support-ticket__client"
                    onClick={onClient}
                    disabled={!onClient}
                    aria-label={onClient ? t.support.openClient : undefined}
                >
                    <Avatar name={who} picture={ticket.client?.picture ?? null} size={44} bordered />

                    <span className="support-ticket__client-body">
                        <span className="support-ticket__client-name">{who}</span>
                        {phone && (
                            <bdi className="support-ticket__client-phone" dir="ltr">
                                {phone}
                            </bdi>
                        )}
                        <span className="support-ticket__client-company">{company}</span>
                    </span>

                    {onClient && <Icon name="chevron" size={16} />}
                </button>

                {/* Where it has got to. Each move asks first — see `SupportStepper`. */}
                <div className="support-ticket__state">
                    <SupportStepper
                        status={status}
                        onPick={(next) => {
                            setStatus(next);
                            onStatus(next);
                        }}
                        disabled={save.loading}
                    />
                </div>

                {/* Their words, read-only, always. */}
                <div className="support-ticket__body">
                    <span className="support-ticket__subject">{ticket.subject}</span>
                    <span className="support-ticket__when">{formatDateTime(ticket.createdAt, lang)}</span>
                    <p className="support-ticket__message">{ticket.message}</p>
                </div>

                {/* The answer, in the box it is written in — and when there already is one, whose it
                    was and when, because editing somebody else's answer should say so first. */}
                <div className="support-ticket__answer">
                    {ticket.repliedAt && (
                        <span className="support-ticket__answer-by">
                            <Icon name="check" size={14} />
                            <span>
                                {ticket.repliedBy ? `${ticket.repliedBy.name} · ` : ""}
                                {formatDateTime(ticket.repliedAt, lang)}
                            </span>
                        </span>
                    )}

                    <TextInput value={reply} onChange={setReply} label={t.support.fields.reply} multiline rows={6} disabled={save.loading} />
                    <p className="side-form__hint">{t.support.replyHint}</p>
                </div>

                {save.error && <p className="side-form__error">{save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button
                    type="button"
                    className="side-form__submit"
                    onClick={() => save.invoke({ method: "PATCH", data: { reply: reply.trim() } })}
                    disabled={save.loading}
                >
                    {save.loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
